// Widget Flags Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';
import {
  getWidgetHostDomain,
  loadWidgetHostPreview,
  widgetHostUnavailableMessage
} from '../utils/widget-iframe-host.js';

const FLAG_REASON_LABELS = {
  inappropriate: 'Inappropriate content',
  copyright: 'Copyright concern',
  spam: 'Spam',
  broken: 'Broken or misleading',
  privacy: 'Privacy concern'
};

class WidgetFlags extends HTMLElement {
  constructor() {
    super();
    this.flags = [];
    this.isLoading = false;
    this.isLoadingFlags = false;
    this.loadError = null;
    this.currentUser = null;
    this.statusFilter = 'pending';
    this.actionHandler = null;
    this.previewErrors = new Map();
    this.previewLoaded = new Set();
    this.handleSectionShown = null;
    this.handleAuthStatusLoaded = null;
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
    this.setupSectionListener();
    if (this.currentUser?.role === 'admin' && this.isVisible()) {
      this.loadFlags();
    }
  }

  disconnectedCallback() {
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    if (this.handleSectionShown) {
      document.removeEventListener('section-shown', this.handleSectionShown);
    }
    const content = this.querySelector('.flags-content');
    if (content && this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }
  }

  setupAuthListener() {
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;
      if (authenticated && user?.role === 'admin') {
        this.currentUser = user;
        if (this.isVisible()) {
          this.loadFlags();
        }
      } else {
        this.currentUser = null;
      }
    };
    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
  }

  setupSectionListener() {
    this.handleSectionShown = (e) => {
      if (e.detail?.section === 'admin-widget-flags' && this.currentUser?.role === 'admin') {
        this.loadFlags();
      }
    };
    document.addEventListener('section-shown', this.handleSectionShown);
  }

  isVisible() {
    return this.offsetParent !== null && !this.style.display.includes('none');
  }

  render() {
    this.innerHTML = `
      <div class="moderation-flags">
        <div class="flags-header">
          ${renderPageTitle('🧩', 'Widget Flags')}
          <p>Review flagged profile widgets</p>
        </div>
        <div class="flags-filters">
          <button type="button" class="filter-btn ${this.statusFilter === 'all' ? 'active' : ''}" data-filter="all">All</button>
          <button type="button" class="filter-btn ${this.statusFilter === 'pending' ? 'active' : ''}" data-filter="pending">Pending</button>
          <button type="button" class="filter-btn ${this.statusFilter === 'approved' ? 'active' : ''}" data-filter="approved">Approved</button>
          <button type="button" class="filter-btn ${this.statusFilter === 'rejected' ? 'active' : ''}" data-filter="rejected">Rejected</button>
        </div>
        <div class="flags-content">${this.renderContent()}</div>
      </div>
    `;

    this.querySelectorAll('.filter-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        this.statusFilter = e.currentTarget.dataset.filter;
        this.updateFilters();
        this.loadFlags();
      });
    });
  }

  updateFilters() {
    this.querySelectorAll('.filter-btn').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.filter === this.statusFilter);
    });
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading widget flags…</div>
        </div>
      `;
    }

    if (this.loadError) {
      return `<div class="error-state">${escapeHtml(this.loadError)}</div>`;
    }

    if (!this.flags.length) {
      const filterLabel = this.statusFilter === 'all' ? '' : ` with status “${this.statusFilter}”`;
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🧩</div>
          <h3>No flags found</h3>
          <p>There are no widget flags${filterLabel}.</p>
        </div>
      `;
    }

    return `
      <div class="flags-list">
        ${this.flags.map((flag) => this.renderFlagItem(flag)).join('')}
      </div>
    `;
  }

  renderFlagItem(flag) {
    const statusClass = flag.status || 'pending';
    const canReview = statusClass === 'pending';
    const widgetName = flag.widget_name || `Widget #${flag.widget_id}`;
    const reasonLabel = FLAG_REASON_LABELS[flag.reason] || flag.reason || 'N/A';
    const previewError = this.previewErrors.get(flag.id);

    let profileHtml = 'Global (not on a profile)';
    if (flag.profile_owner_username) {
      const sectionSuffix = flag.section_id
        ? ` · slot ${escapeHtml(flag.section_id)}`
        : '';
      profileHtml = `
        <a href="/profile/${encodeURIComponent(flag.profile_owner_username)}" class="flag-profile-link">
          @${escapeHtml(flag.profile_owner_username)}
        </a>${sectionSuffix}
      `;
    }

    return `
      <div class="flag-item" data-flag-id="${flag.id}" data-widget-id="${flag.widget_id}">
        <div class="flag-header">
          <h3 class="flag-title">${escapeHtml(widgetName)}</h3>
          <span class="flag-status ${statusClass}">${escapeHtml(statusClass)}</span>
        </div>

        <div class="flag-widget-preview" data-preview-container="${flag.id}">
          <iframe
            class="flag-widget-preview-frame"
            data-widget-id="${flag.widget_id}"
            data-flag-id="${flag.id}"
            title="Preview of ${escapeHtml(widgetName)}"
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
          ></iframe>
          ${previewError ? `<p class="flag-widget-preview-error" role="status">${escapeHtml(previewError)}</p>` : ''}
        </div>

        <div class="flag-details">
          <div class="flag-detail">
            <span class="flag-detail-label">Reason</span>
            <span class="flag-detail-value">${escapeHtml(reasonLabel)}</span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Flagged by</span>
            <span class="flag-detail-value">${escapeHtml(flag.flagged_by_username || 'Unknown')}</span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Flagged at</span>
            <span class="flag-detail-value">${this.formatDate(flag.created_at)}</span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Profile context</span>
            <span class="flag-detail-value">${profileHtml}</span>
          </div>
          ${flag.reviewed_at ? `
            <div class="flag-detail">
              <span class="flag-detail-label">Reviewed at</span>
              <span class="flag-detail-value">${this.formatDate(flag.reviewed_at)}</span>
            </div>
          ` : ''}
        </div>

        ${canReview ? `
          <div class="flag-actions">
            <button type="button" class="flag-action-btn approve"
                    data-flag-id="${flag.id}" data-action="approve">
              Dismiss flag
            </button>
            <button type="button" class="flag-action-btn reject"
                    data-flag-id="${flag.id}" data-action="reject">
              Uphold flag
            </button>
            <p class="form-text flag-action-hint">
              <strong>Dismiss</strong> means the widget is fine — remove this report.
              <strong>Uphold</strong> keeps the widget gated or hidden for viewers.
              This is not the same as community catalog approval.
            </p>
          </div>
        ` : ''}
      </div>
    `;
  }

  async loadFlags() {
    if (this.isLoadingFlags) return;
    if (!this.currentUser || this.currentUser.role !== 'admin') return;
    if (!this.isVisible()) return;

    this.isLoadingFlags = true;
    this.isLoading = true;
    this.loadError = null;
    this.previewErrors = new Map();
    this.previewLoaded = new Set();
    this.updateContent();

    try {
      const url = this.statusFilter === 'all'
        ? '/admin/widget-flags'
        : `/admin/widget-flags?status=${this.statusFilter}`;
      const res = await window.API.request(url);
      this.flags = res.flags || [];
    } catch (err) {
      window.logger?.error('Failed to load widget flags:', err);
      this.flags = [];
      this.loadError = 'Failed to load widget flags. Please try again.';
    } finally {
      this.isLoading = false;
      this.isLoadingFlags = false;
      this.updateContent();
    }
  }

  updateContent() {
    const content = this.querySelector('.flags-content');
    if (content) {
      content.innerHTML = this.renderContent();
      this.attachActionListeners();
      this.schedulePreviewLoads();
    }
  }

  schedulePreviewLoads() {
    const frames = this.querySelectorAll('.flag-widget-preview-frame:not([data-preview-scheduled])');
    if (!frames.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const iframe = entry.target;
          observer.unobserve(iframe);
          this.loadPreviewForIframe(iframe);
        }
      }, { rootMargin: '100px' });
      frames.forEach((frame) => {
        frame.dataset.previewScheduled = '1';
        observer.observe(frame);
      });
      return;
    }

    frames.forEach((frame) => {
      frame.dataset.previewScheduled = '1';
      this.loadPreviewForIframe(frame);
    });
  }

  async loadPreviewForIframe(iframe) {
    const widgetId = iframe.dataset.widgetId;
    const flagId = iframe.dataset.flagId;
    if (!widgetId || this.previewLoaded.has(flagId)) return;

    this.previewLoaded.add(flagId);
    try {
      const res = await window.API.request(`/widgets/${widgetId}/preview-token`, { method: 'POST' });
      const host = getWidgetHostDomain();
      loadWidgetHostPreview(
        iframe,
        `${host}/render/preview/${widgetId}?token=${encodeURIComponent(res.token)}`,
        {
          widgetId,
          onError: () => {
            this.previewErrors.set(Number(flagId), widgetHostUnavailableMessage());
            this.updatePreviewErrorDom(flagId);
          }
        }
      );
    } catch (err) {
      window.logger?.warn('Widget flag preview failed:', err);
      this.previewErrors.set(Number(flagId), widgetHostUnavailableMessage());
      this.updatePreviewErrorDom(flagId);
    }
  }

  updatePreviewErrorDom(flagId) {
    const container = this.querySelector(`[data-preview-container="${flagId}"]`);
    if (!container) return;
    const msg = this.previewErrors.get(Number(flagId));
    let el = container.querySelector('.flag-widget-preview-error');
    if (msg) {
      if (!el) {
        el = document.createElement('p');
        el.className = 'flag-widget-preview-error';
        el.setAttribute('role', 'status');
        container.appendChild(el);
      }
      el.textContent = msg;
    } else if (el) {
      el.remove();
    }
  }

  attachActionListeners() {
    const content = this.querySelector('.flags-content');
    if (!content) return;

    if (this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }

    this.actionHandler = async (e) => {
      const btn = e.target.closest('.flag-action-btn');
      if (!btn) return;

      const flagId = btn.dataset.flagId;
      const action = btn.dataset.action;
      if (!flagId || !action) return;

      btn.disabled = true;
      const status = action === 'approve' ? 'approved' : 'rejected';

      try {
        await window.API.request(`/admin/widget-flags/${flagId}/review`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status })
        });
        await this.loadFlags();
      } catch (err) {
        window.logger?.error('Failed to review widget flag:', err);
        btn.disabled = false;
        alert('Failed to review flag. Please try again.');
      }
    };

    content.addEventListener('click', this.actionHandler);
  }

  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    if (this.isVisible() && user?.role === 'admin') {
      this.loadFlags();
    }
  }
}

customElements.define('widget-flags', WidgetFlags);
